import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, TicketPercent, LogOut, Menu, X, Inbox, Network, Link as LinkIcon, Sparkles, Target, MessageCircle, Wallet, UserPlus, PhoneCall, Megaphone, Cpu } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";

const ADMIN_LINKS = [
  { to: "/admin", icon: LayoutDashboard, label: "Panoramica", testid: "admin-nav-overview", end: true },
  { to: "/admin/users", icon: Users, label: "Utenti", testid: "admin-nav-users" },
  { to: "/admin/leads", icon: FileText, label: "Lead globali", testid: "admin-nav-leads" },
  { to: "/admin/network-applications", icon: UserPlus, label: "Candidature professionisti", testid: "admin-nav-network-applications" },
  { to: "/admin/pa-requests", icon: PhoneCall, label: "Richieste contatto PA", testid: "admin-nav-pa-requests" },
  { to: "/admin/marketing", icon: Megaphone, label: "Marketing PA", testid: "admin-nav-marketing" },
  { to: "/admin/pa-ai-usage", icon: Cpu, label: "Consumi AI PA", testid: "admin-nav-pa-ai-usage" },
  { to: "/admin/promo-codes", icon: TicketPercent, label: "Codici promo", testid: "admin-nav-promo" },
  { to: "/admin/payments", icon: Wallet, label: "Pagamenti", testid: "admin-nav-payments" },
];

const AGENT_LINKS = [
  { to: "/agent/leads", icon: Inbox, label: "I miei lead", testid: "admin-nav-my-leads" },
  { to: "/agent/network", icon: Network, label: "Rete agenti", testid: "admin-nav-my-network" },
  { to: "/agent/link", icon: LinkIcon, label: "Marketing & Lead", testid: "admin-nav-my-link" },
  { to: "/agent/social-coach", icon: Sparkles, label: "Social Coach", testid: "admin-nav-social-coach" },
  { to: "/agent/strategy-coach", icon: Target, label: "Strategy Coach", testid: "admin-nav-strategy-coach" },
  { to: "/agent/sara", icon: MessageCircle, label: "Sara", testid: "admin-nav-sara" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.ruolo !== "admin") nav("/agent");
  }, [user, nav]);

  if (!user || user.ruolo !== "admin") return null;

  const sidebar = (
    <aside className="vg-sidebar w-64 h-screen sticky top-0 overflow-y-auto p-5 flex flex-col" data-testid="admin-sidebar">
      <div className="mb-6 px-2"><Logo size={22} white /></div>

      <p className="text-[10px] uppercase font-bold tracking-wider mb-2 px-3" style={{ color: "#B40101" }}>Admin Panel</p>
      <nav className="space-y-1 mb-5">
        {ADMIN_LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
            data-testid={l.testid}
            className={({ isActive }) => `vg-sidebar-link ${isActive ? "active" : ""}`}>
            <l.icon size={18} /> {l.label}
          </NavLink>
        ))}
      </nav>

      <p className="text-[10px] uppercase font-bold tracking-wider mb-2 px-3" style={{ color: "#C9C9C9" }}>La tua rete</p>
      <nav className="space-y-1 flex-1">
        {AGENT_LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
            data-testid={l.testid}
            className={({ isActive }) => `vg-sidebar-link ${isActive ? "active" : ""}`}>
            <l.icon size={18} /> {l.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 pt-4 mt-4">
        <div className="px-3 mb-3">
          <p className="text-sm font-semibold text-white truncate">{user.nome} {user.cognome}</p>
          <p className="text-xs text-gray-400 truncate">{user.email}</p>
          <span className="inline-block text-[10px] font-bold mt-1 px-2 py-0.5 rounded-full" style={{ background: "#B40101" }}>ADMIN · FPA</span>
        </div>
        <button onClick={() => { logout(); nav("/login"); }} className="vg-sidebar-link w-full" data-testid="admin-logout">
          <LogOut size={18} /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen" style={{ background: "#F5F5F5" }}>
      <div className="md:hidden flex items-center justify-between bg-[#404040] text-white p-4">
        <Logo size={20} white />
        <button onClick={() => setOpen(!open)} data-testid="admin-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <div className="flex">
        <div className="hidden md:block">{sidebar}</div>
        {open && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <div className="relative">{sidebar}</div>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
